import { makeObservable, observable, action } from 'mobx';
import { Subject } from 'rxjs';
import type ContractDraftContext from '../../drafts/ContractDraftContext';
import { NodeBaseDto } from '../../types';
import { InputBaseModel } from '..';
import { SourceModelBase } from '../../../custom_shared/models';

export default abstract class NodeBaseModel {
    get history() {
        return this.context.history;
    }
    get drafts() {
        return this.context.drafts;
    }
    get sourceRequests() {
        return this.context.sourceRequests;
    }

    readonly id: string;

    readonly type: string;

    readonly name: string;

    inputAvailability: NonNullable<NodeBaseDto['inputAvailability']>;

    readonly inputValueChangeSubject = new Subject<InputBaseModel>();

    readonly inputValidationSubject = new Subject<InputBaseModel>();

    childNodes: NodeBaseModel[] = [];

    hasError: boolean = false;

    constructor(
        data: NodeBaseDto,
        readonly context: ContractDraftContext
    ) {
        this.id = data.id;
        this.type = data.type;
        this.name = data.name;
        this.inputAvailability = data.inputAvailability ?? {};

        makeObservable(this, {
            childNodes: observable,
            hasError: observable,
            setHasError: action,
            setChildNodes: action
        });
    }

    setChildNodes(childNodes: NodeBaseModel[]) {
        this.childNodes = childNodes;
    }

    setHasError(hasError: boolean) {
        this.hasError = hasError;
    }

    getInputByGuid(guid: string): InputBaseModel | undefined {
        const input = this.inputs.find(i => i.guid === guid);

        if (input) {
            return input;
        }

        for (const childNode of this.childNodes) {
            const childInput = childNode.getInputByGuid(guid);

            if (childInput) {
                return childInput;
            }
        }

        return undefined;
    }

    get isValid(): boolean {
        return this.inputs.filter(i => i.required).every(i => i.hasValue);
    }

    abstract refreshExpandedState(): void;

    abstract update(data: NodeBaseDto, sources: SourceModelBase[]): void;

    abstract get inputs(): InputBaseModel[];

    abstract get dto(): NodeBaseDto;

    abstract getInputsAtSameLevel(inputGuid: string): InputBaseModel[];
}
